import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';
import { SUCCESS, ERROR, WARNING, INFO } from '../theme/adminColors';

// Create context
const ThemeContext = createContext();

// Custom hook to use the theme context
export const useCustomTheme = () => {
  return useContext(ThemeContext);
};

export const ThemeProvider = ({ children }) => {
  const [primaryColor, setPrimaryColor] = useState('#1976d2');
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [coverName, setCoverName] = useState('');
  const [coverDescription, setCoverDescription] = useState('');
  const theme = useMemo(() => {
    return createTheme({
      palette: {
        primary: {
          main: primaryColor,
        },
        success: {
          main: SUCCESS.main,
          dark: SUCCESS.dark,
          light: SUCCESS.light,
          contrastText: SUCCESS.contrastText,
        },
        error: {
          main: ERROR.main,
          dark: ERROR.dark,
          light: ERROR.light,
          contrastText: ERROR.contrastText,
        },
        warning: {
          main: WARNING.main,
          dark: WARNING.dark,
          light: WARNING.light,
          contrastText: WARNING.contrastText,
        },
        info: {
          main: INFO.main,
          dark: INFO.dark,
          light: INFO.light,
          contrastText: INFO.contrastText,
        },
      },
      typography: {
        fontFamily: [
          'Roboto',
          '"Helvetica Neue"',
          'Arial',
          'sans-serif',
        ].join(','),
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 8,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
    });
  }, [primaryColor]);

  // Load merchant theming from localStorage
  useEffect(() => {
    const storedMerchant = localStorage.getItem('merchant');

    if (storedMerchant) {
      try {
        const parsedMerchant = JSON.parse(storedMerchant);

        if (parsedMerchant.primary_color) {
          setPrimaryColor(parsedMerchant.primary_color);
        }

        if (parsedMerchant.cover_photo) {
          setCoverPhoto(parsedMerchant.cover_photo);
        }

        if (parsedMerchant.cover_name) {
          setCoverName(parsedMerchant.cover_name);
        }

        if (parsedMerchant.cover_description) {
          setCoverDescription(parsedMerchant.cover_description);
        }
      } catch (error) {
        console.error('Error parsing merchant from localStorage:', error);
      }
    }
  }, []);

  // Update theme when merchant changes
  const updateMerchantTheme = (merchantData) => {
    if (merchantData) {
      if (merchantData.primary_color) {
        setPrimaryColor(merchantData.primary_color);
      }

      if (merchantData.cover_photo) {
        setCoverPhoto(merchantData.cover_photo);
      }

      if (merchantData.cover_name !== undefined) {
        setCoverName(merchantData.cover_name);
      }

      if (merchantData.cover_description !== undefined) {
        setCoverDescription(merchantData.cover_description);
      }
    }
  };

  const value = {
    theme,
    primaryColor,
    coverPhoto,
    coverName,
    coverDescription,
    updateMerchantTheme,

  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Export ThemeProvider as CustomThemeProvider for backward compatibility
export const CustomThemeProvider = ThemeProvider;
